'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { HistoryResult, SearchResult } from './types';
import { SportIcon } from '@/components/icons/svg-icons';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { useRouter } from '@/navigation';
import { searchService } from '@/services/search.service';
import { useAccountInfo } from '@/hooks/query/account';
import { fetchGetSearchResult, fetchPostRemoveHistory, useGetSearchHistory } from '@/hooks/query/use-search-data';
import { CloseRoundIcon } from '@/components/icons/svg-icons';
import { useEffect, useState } from 'react';
import { useSiteStore } from '@/store/site';
import { useAppearanceStore } from '@/store/appearance';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { getAuthToken, useAuthToken } from '@/store/auth';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import { searchStyle } from '../search-component.style';
import { ViewEnum } from '@/types';
import { getForbiddenSport } from '@/services/settings.service';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';

interface SearchContainerProps {
	searchResults: Array<SearchResult>;
	setSearchResults: (value: Array<SearchResult>) => void;
}
export const SearchContainer = ({ searchResults, setSearchResults }: SearchContainerProps) => {
	const t = useTranslations('General');
	const { user } = useAccountInfo();
	const input = searchService.searchInputValue;
	const { data } = useGetSearchHistory({
		userlanguage: user?.Lang ?? 'en-US',
	}) ?? [];
	const apiDomain = useSiteStore(state => state.apiDomain);
	const [searchHistorys, setSearchHistorys] = useState<Array<HistoryResult>>(data ?? []);
	const view = useAppearanceStore(state => state.view);
	
	useEffect(() => {
		setSearchHistorys(data ?? [])
	}, [data]);
	const clearAllHistory = () => {
		fetchPostRemoveHistory(apiDomain, { userlanguage: user?.Lang ?? 'en-US', keywordId: '' }).then(res => {
			if (res.data) {
				setSearchHistorys(res.data)
			}
		});
	}
	return (
		<div className={clsx('search__container', { 'scrollbar': view === ViewEnum.mars }, searchStyle.container)}>
			{input !== '' ? 
				<>
					<div className="search__head">
						<div className="search__head-text">{`${searchResults.length} ${t('lbl_SearchResults')}`}</div>
					</div>
					<SearchItems 
						searchResults={searchResults}
					/>
				</> : 
				searchHistorys.length === 0 ? 
					<div className="empty">
						<div className="empty__img empty__img--search"></div>
						<div className="empty__title">{t('lbl_SearchLeague')}</div>
					</div>
					:
					<>
						<div className="search__head">
							<div className="search__head-text">{t('lbl_SearchHistory')}</div>
							<div className="search__head-link" onClick={() => clearAllHistory()}>{t('lbl_Clear')}</div>
						</div>
						<SearchHashTags 
							historys={searchHistorys} 
							setSearchHistorys={setSearchHistorys}
							setSearchResults={setSearchResults}
						/>
					</>
			}
		</div>
	)
}
interface SearchItemsProps {
	searchResults: Array<SearchResult>;
}
const SearchItems = ({ searchResults }: SearchItemsProps) => {
	const taggedElement = useAnalyseAttribute('switch-to-single-match-via-search-result');
	return (
		<div 
			ref={taggedElement}
			className="search__items"
		>
			{searchResults.map((searchResult, index) => {
				return (
					<SearchItem 
						key={index}
						searchResult={searchResult}
					/>
				)
			})}
		</div>
	)
}
interface SearchItemProps {
	key: number;
	searchResult: SearchResult;
}
const SearchItem = ({ searchResult }: SearchItemProps) => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();
	const router = useRouter();
	const { filterSabaString } = useFilterSabaString();
	const { disableParlay } = useOddsPageSwitcher();
	const singleMatchHref = matchEquals(searchResult.Market)
		.on('l', () => `/live/${searchResult.SportType}/${searchResult.LeagueID}/${searchResult.MatchID}`)
		.on('outright', () => `/sports/${searchResult.LeagueID}/outright?matchId=${searchResult.MatchID}`)
		.otherwise(() => `/sports/${searchResult.SportType}/${searchResult.Market}/${searchResult.LeagueID}/${searchResult.MatchID}`);

	const getMarketName = (market?: string) => {
		switch (market) {
			case 'l':
				return t('lbl_live');
			case 't':
				return t('lbl_today');
			case 'e':
				return t('lbl_early');
			case 'outright':
				return t('lbl_999');
			default:
				return '';
		}
	}
	const handleClickSearchItem = () => {
		// 點選搜尋結果需關閉parlay mode, 不然如果該match不支援parlay會進入empty page
		disableParlay();
		if (view == ViewEnum.mars && breakpoint <= Breakpoint.sm && searchResult.Market != 'outright') {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 2,
				action: 20
			}
			setKafkaNormal(kafkaData);
		}
		router.push(singleMatchHref);
		searchService.displaySearchModal(false);
	}
	return (
		<div
			className="search__item"
			onClick={() => handleClickSearchItem()}
		>
			<div className="search__sport">
				<SportIcon sportId={String(searchResult.SportType)} />
				<div className="search__sport-text">{filterSabaString(searchResult.LeagueName ?? '')}</div>
				<div className={clsx('search__label', searchStyle.label)}>{getMarketName(searchResult.Market)}</div>
			</div>
			<div className="search__match">{searchResult.MatchName}</div>
			<div className="search__time">{searchResult.KickOffTime}</div>
		</div>
	)
}
interface SearchHashTags {
	historys?: Array<HistoryResult>;
	setSearchHistorys: (value: Array<HistoryResult>) => void;
	setSearchResults: (value: Array<SearchResult>) => void;
}
const SearchHashTags = ({ historys, setSearchHistorys, setSearchResults }: SearchHashTags) => {
	return (
		<div className="search__hashtags">
			{historys && historys.length > 0 ? historys.map((history) => {
				return (
					<SearchHashTag 
						key={history.KeywordId}
						history={history}
						setSearchHistorys={setSearchHistorys}
						setSearchResults={setSearchResults}
					/>
				)
			}): null}
		</div>
	)
}
interface SearchHashTag {
	history?: HistoryResult;
	setSearchHistorys: (value: Array<HistoryResult>) => void;
	setSearchResults: (value: Array<SearchResult>) => void;
}
const SearchHashTag = ({ history, setSearchHistorys, setSearchResults }: SearchHashTag) => {
	const { user } = useAccountInfo();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const removeHistory = (keywordId: string) => {
		fetchPostRemoveHistory(apiDomain, { userlanguage: user?.Lang ?? 'en-US', keywordId }).then(res => {
			if (res.data) {
				setSearchHistorys(res.data)
			}
		});
	}
	const { filterSabaString } = useFilterSabaString();
	const isBefore = !Boolean(useAuthToken());
	const visitorInfo = useAtomValue(visitorAtom);
	const useToken = isBefore ? visitorInfo?.AccessToken : getAuthToken();
	const view = useAppearanceStore(state => state.view);
	const handleClickHashTag = (Keyword?: string) => {
		searchService.setSearchInput(Keyword?? 'empty');
		fetchGetSearchResult(apiDomain, { searchinput: Keyword, userlanguage: user?.Lang ?? 'en-US' }, useToken).then(res => {
			if (res.data) {
				const searchResults = res.data.filter(searchResult => !getForbiddenSport(view).includes(searchResult.SportType?.toString() || ''));
				setSearchResults(searchResults);
			}
		});
	}
	return (
		<>
			{history && 
				<div className="hashtag">
					<div className="hashtag__text" onClick={() => 
						handleClickHashTag(history.Keyword)
					}
					>{filterSabaString(history.Keyword ?? '')}
					</div>
					<button
						className="btn btn--icon"
						onClick={() => removeHistory(history.KeywordId || '')}
					>
						<CloseRoundIcon  />
					</button>
				</div>}
		</>
	)
} 
